<script setup lang="ts">
import { computed } from "vue";

import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import type { StatusItem } from "@/store/search/searchMenu";
import { useSearchMenuStore } from "@/store/search/searchMenu";

const props = defineProps<{ statuses: StatusItem[] }>();

// update global selection based on changes in this list only
function updateSelectionOfStatuses(newSelection: StatusItem[]): Set<string> {
  const globalSelection = new Set(useSearchMenuStore().selectedStatusIds);
  const newSelectedIds = new Set(newSelection.map((s) => s.id));
  const statusIdsInThisList = new Set(props.statuses.map((s) => s.id));

  // remove deselected statuses (only those from this list)
  for (const id of statusIdsInThisList) {
    if (!newSelectedIds.has(id)) {
      globalSelection.delete(id);
    }
  }

  // add newly selected statuses
  for (const id of newSelectedIds) {
    globalSelection.add(id);
  }

  return globalSelection;
}

const selectedStatuses = computed({
  get: () => {
    // Only return selected statuses that belong to this list
    const allSelected = useSearchMenuStore().selectedStatusItems;
    const statusIdsInThisList = new Set(props.statuses.map((s) => s.id));
    return allSelected.filter((item) => statusIdsInThisList.has(item.id));
  },
  set: (values: StatusItem[]) => {
    useSearchMenuStore().selectedStatusIds = updateSelectionOfStatuses(values);
  },
});
</script>

<template>
  <SelectableList v-model="selectedStatuses" :values="statuses">
    <template #before="{ item }">
      <TypeDot :color="item.color" />
    </template>
    <template #default="{ item }">
      {{ item.name }}
    </template>
  </SelectableList>
</template>
